import graphqlClient from './graphqlClient';
import { getPurchekThresholdsForQuery, getContainmentThresholdsForQuery, getCartManagerThresholdsForQuery } from '../utils/recommendationLogic';
import { useConfigurationStore } from '../stores';
import { environment } from '../environment/environment';
import { NoteAuditLogEntry, resolveAuditLogUsers } from './auditLogService';

export interface AggregateCount {
  aggregate: {
    count: number;
  };
}

export interface AggregateAvg {
  aggregate: {
    avg: number;
  };
}

export interface BrandNoteUser {
  id: string;
  name: string;
}

export interface BrandNote {
  id: string;
  comment: string;
  created_at?: string;
  updated_at?: string;
  user?: BrandNoteUser;
}

export interface BrandData {
  name: string;
  ultimate_parent?: string;
  is_top_retailer?: boolean;
  estimated_no_of_carts_per_store?: number;
  total_pois?: AggregateCount;
  customers?: AggregateCount;
  purchek_recommended?: AggregateCount;
  containment_recommended?: AggregateCount;
  cart_manager_recommended?: AggregateCount;
  subscription_recommended?: AggregateCount;
  paired_wheel_recommended?: AggregateCount;
  purchek_acquired?: AggregateCount;
  containment_acquired?: AggregateCount;
  cart_manager_acquired?: AggregateCount;
  subscription_acquired?: AggregateCount;
  paired_wheel_acquired?: AggregateCount;
  brand_notes?: BrandNote[];
  brand_notes_count?: AggregateCount;
}

export interface BrandResponse {
  udm_brand: BrandData[];
}

export interface FilterParams {
  opportunitySize?: string;
  channel?: string;
  retailerSize?: string;
  neighborhoodGKAP?: string;
  crimeIndex?: string;
}

export interface ServiceableSitesResponse {
  udm_poi_aggregate: AggregateCount;
}

export interface KeyInsightsResponse {
  total_serviceable_sites: AggregateCount;
  total_acquired_sites: AggregateCount;
  retailers_no_penetration: AggregateCount;
  high_crime_sites_count: AggregateCount;
  high_gkap_sites_count: AggregateCount;
  total_opportunities: AggregateCount;
  new_install_count: AggregateCount;
  existing_install_count: AggregateCount;
}

export interface KeyInsightsData {
  totalServiceableSites: number;
  totalAcquiredSites: number;
  retailersWithNoPenetration: number;
  highCrimeSites: number;
  highGkapSites: number;
  totalOpportunities: number;
  newInstallCount: number;
  existingInstallCount: number;
}

/**
 * Fetch all Key Insights metrics
 * Calculates date 6 months ago from today for new install filtering
 */
export const fetchKeyInsights = async (): Promise<KeyInsightsData> => {
  try {
    const query = `
      query keyInsights @cached {
        total_serviceable_sites: udm_poi_aggregate(where: {closed_on: {_is_null: true}}) {
          aggregate {
            count
          }
        }
        total_acquired_sites: udm_poi_aggregate(where: {closed_on: {_is_null: true}, customer_id: {_is_null: false}}) {
          aggregate {
            count
          }
        }
        retailers_no_penetration: udm_poi_aggregate(where: {nearby_penetration: {penetration: {_eq: 0}}}) {
          aggregate {
            count
          }
        }
        high_crime_sites_count: udm_poi_aggregate(where: {geo: {total_crime_incidents: {_gt: 200}}}) {
          aggregate {
            count
          }
        }
        high_gkap_sites_count: udm_poi_aggregate(where: {nearby_penetration: {penetration: {_gt: 20}}}) {
          aggregate {
            count
          }
        }
        total_opportunities: udm_poi_aggregate(where: {_and: [{closed_on: {_is_null: true}}, {_or: [{_and: [{purchek_install_date: {_is_null: true}}, {_or: [{nearby_penetration: {purchek_customer_count: {_gte: 5}}}, {nearby_penetration: {penetration_purchek: {_gte: 75}}}, {geo: {total_crime_incidents: {_gte: 5000}}}]}]}, {_and: [{containment_install_date: {_is_null: true}}, {_or: [{nearby_penetration: {containment_customer_count: {_gte: 5}}}, {nearby_penetration: {penetration_containment: {_gte: 75}}}, {geo: {total_crime_incidents: {_gte: 5000}}}, {geo: {penalty_for_cart_owner: {_eq: true}}}, {nearby_penetration: {plaza_cm_customer_count: {_gt: 0}}}]}]}, {_and: [{cart_manager_install_date: {_is_null: true}}, {_or: [{geo: {no_of_cold_days: {_gte: 90}}}, {geo: {no_of_hot_days: {_gte: 90}}}]}]}]}]}) {
          aggregate {
            count
          }
        }
        new_install_count: udm_poi_aggregate(where: {_and: [{closed_on: {_is_null: true}}, {purchek_install_date: {_is_null: true}}, {containment_install_date: {_is_null: true}}, {cart_manager_install_date: {_is_null: true}}, {subscription_start_date: {_is_null: true}}, {paired_wheel_install_date: {_is_null: true}}, {_or: [{nearby_penetration: {purchek_customer_count: {_gte: 5}}}, {nearby_penetration: {penetration_purchek: {_gte: 75}}}, {geo: {total_crime_incidents: {_gte: 5000}}}, {nearby_penetration: {containment_customer_count: {_gte: 5}}}, {nearby_penetration: {penetration_containment: {_gte: 75}}}, {geo: {penalty_for_cart_owner: {_eq: true}}}, {nearby_penetration: {plaza_cm_customer_count: {_gt: 0}}}, {geo: {no_of_cold_days: {_gte: 90}}}, {geo: {no_of_hot_days: {_gte: 90}}}]}]}) {
          aggregate {
            count
          }
        }
        existing_install_count: udm_poi_aggregate(where: {_and: [{closed_on: {_is_null: true}}, {_or: [{purchek_install_date: {_is_null: false}}, {containment_install_date: {_is_null: false}}, {cart_manager_install_date: {_is_null: false}}, {subscription_start_date: {_is_null: false}}, {paired_wheel_install_date: {_is_null: false}}]}, {_or: [{nearby_penetration: {purchek_customer_count: {_gte: 5}}}, {nearby_penetration: {penetration_purchek: {_gte: 75}}}, {geo: {total_crime_incidents: {_gte: 5000}}}, {nearby_penetration: {containment_customer_count: {_gte: 5}}}, {nearby_penetration: {penetration_containment: {_gte: 75}}}, {geo: {penalty_for_cart_owner: {_eq: true}}}, {nearby_penetration: {plaza_cm_customer_count: {_gt: 0}}}, {geo: {no_of_cold_days: {_gte: 90}}}, {geo: {no_of_hot_days: {_gte: 90}}}]}]}) {
          aggregate {
            count
          }
        }
      }
    `;

    const response = await graphqlClient.request<KeyInsightsResponse>(query);

    return {
      totalServiceableSites: response.total_serviceable_sites?.aggregate?.count || 0,
      totalAcquiredSites: response.total_acquired_sites?.aggregate?.count || 0,
      retailersWithNoPenetration: response.retailers_no_penetration?.aggregate?.count || 0,
      highCrimeSites: response.high_crime_sites_count?.aggregate?.count || 0,
      highGkapSites: response.high_gkap_sites_count?.aggregate?.count || 0,
      totalOpportunities: response.total_opportunities?.aggregate?.count || 0,
      newInstallCount: response.new_install_count?.aggregate?.count || 0,
      existingInstallCount: response.existing_install_count?.aggregate?.count || 0
    };
  } catch (error: any) {
    console.error('fetchKeyInsights error', error);
    return {
      totalServiceableSites: 0,
      totalAcquiredSites: 0,
      retailersWithNoPenetration: 0,
      highCrimeSites: 0,
      highGkapSites: 0,
      totalOpportunities: 0,
      newInstallCount: 0,
      existingInstallCount: 0
    };
  }
};

/**
 * Fetch total serviceable sites (open POIs)
 * Serviceable sites are locations where closed_on is null
 */
export const fetchTotalServiceableSites = async (): Promise<number> => {
  try {
    const query = `
      query TotalServiceableSites @cached {
        udm_poi_aggregate(where: {closed_on: {_is_null: true}}) {
          aggregate {
            count
          }
        }
      }
    `;

    const response = await graphqlClient.request<ServiceableSitesResponse>(query);
    return response.udm_poi_aggregate?.aggregate?.count || 0;
  } catch (error: any) {
    console.error('fetchTotalServiceableSites error', error);
    return 0;
  }
};

/**
 * Fetch notes for a specific brand by name
 */
export const fetchBrandNotes = async (brandName: string): Promise<BrandNote[]> => {
  try {
    const query = `
      query GetBrandNotes($brandName: String!) {
        udm_brand(where: {name: {_eq: $brandName}}) {
          brand_notes(order_by: {created_at: desc}) {
            id
            comment
            created_at
            updated_at
            user {
              id
              name
            }
          }
        }
      }
    `;

    const response = await graphqlClient.request<{
      udm_brand: Array<{ brand_notes: BrandNote[] }>;
    }>(query, { brandName });

    if (response.udm_brand && response.udm_brand.length > 0) {
      return response.udm_brand[0].brand_notes || [];
    }
    return [];
  } catch (error: any) {
    console.error('fetchBrandNotes error', error);
    return [];
  }
};

/**
 * Submit a new note for a brand
 */
export const submitBrandNote = async (
  brandName: string,
  userId: string,
  comment: string
): Promise<BrandNote | null> => {
  try {
    const mutation = `
      mutation InsertBrandNote($brandName: String!, $userId: uuid!, $comment: String!) {
        insert_app_brand_notes_one(
          object: {
            brand_name: $brandName
            user_id: $userId
            comment: $comment
          }
        ) {
          id
          comment
          created_at
          updated_at
          user {
            id
            name
          }
        }
      }
    `;

    const response = await graphqlClient.request<{
      insert_app_brand_notes_one: BrandNote;
    }>(mutation, {
      brandName,
      userId,
      comment
    });

    return response.insert_app_brand_notes_one || null;
  } catch (error: any) {
    console.error('submitBrandNote error', error);
    return null;
  }
};

/**
 * Update an existing note
 */
export const updateBrandNote = async (
  noteId: string,
  comment: string
): Promise<BrandNote | null> => {
  try {
    const mutation = `
      mutation UpdateBrandNote($id: uuid!, $comment: String!) {
        update_app_brand_notes_by_pk(pk_columns: {id: $id}, _set: {comment: $comment}) {
          id
          comment
          created_at
          updated_at
          user {
            id
            name
          }
        }
      }
    `;

    const response = await graphqlClient.request<{
      update_app_brand_notes_by_pk: BrandNote;
    }>(mutation, {
      id: noteId,
      comment
    });

    return response.update_app_brand_notes_by_pk || null;
  } catch (error: any) {
    console.error('updateBrandNote error', error);
    return null;
  }
};

/**
 * Delete a note
 */
export const deleteBrandNote = async (noteId: string): Promise<boolean> => {
  try {
    const mutation = `
      mutation DeleteBrandNote($id: uuid!) {
        delete_app_brand_notes_by_pk(id: $id) {
          id
        }
      }
    `;

    await graphqlClient.request<{
      delete_app_brand_notes_by_pk: { id: string };
    }>(mutation, {
      id: noteId
    });

    return true;
  } catch (error: any) {
    console.error('deleteBrandNote error', error);
    return false;
  }
};

/**
 * Fetch the change history for a brand's notes (backed by app.audit_log,
 * entity_type='brand_note', populated by a DB trigger on app.brand_notes)
 */
export const fetchBrandAuditLog = async (brandName: string): Promise<NoteAuditLogEntry[]> => {
  try {
    const query = `
      query GetBrandAuditLog($brandName: String!) {
        app_audit_log(
          where: { brand_id: { _eq: $brandName }, entity_type: { _eq: "brand_note" } }
          order_by: { timestamp: desc }
        ) {
          id
          entity_id
          action
          old_value
          new_value
          user_id
          timestamp
        }
      }
    `;

    const response = await graphqlClient.request<{
      app_audit_log: Array<{
        id: string;
        entity_id: string;
        action: string;
        old_value: string | null;
        new_value: string | null;
        user_id: string;
        timestamp: string;
      }>;
    }>(query, { brandName });

    return await resolveAuditLogUsers(response.app_audit_log);
  } catch (error: any) {
    console.error('fetchBrandAuditLog error', error);
    return [];
  }
};

export const fetchBrandData = async (limit: number = 10, offset: number = 0, solutionFilter?: string, brandName?: string, sortBy: string = 'Top Retailers', purchekConfig?: any, containmentConfig?: any, cartManagerConfig?: any): Promise<BrandData[]> => {
  try {
    // Get active configuration from store
    const store = useConfigurationStore.getState();

    // Use provided config or get from store
    const activePurchekConfig = purchekConfig || store.purchek;
    const activeContainmentConfig = containmentConfig || store.containment;
    const activeCartManagerConfig = cartManagerConfig || store.cartManager;

    // Get dynamic thresholds from active config
    const purchekThresholds = getPurchekThresholdsForQuery(activePurchekConfig);
    const containmentThresholds = getContainmentThresholdsForQuery(activeContainmentConfig);
    const cartManagerThresholds = getCartManagerThresholdsForQuery(activeCartManagerConfig);

    // Calculate date 6 months ago from today
    const today = new Date();
    const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
    const sixMonthsAgoStr = sixMonthsAgo.toISOString().split('T')[0];
    console.log('sixMonthsAgoStr', sixMonthsAgoStr);

    // Build where clause based on solution filter and brand name
    let whereClause = '';
    const conditions: string[] = [];

    // Add brand name substring match filter if provided (case-insensitive)
    if (brandName) {
      conditions.push(`name: { _ilike: "%${brandName}%" }`);
    }

    // Add solution filter
    if (solutionFilter && solutionFilter !== 'all') {
      const filterMap: { [key: string]: string } = {
        'purchek': 'purchek_install_date',
        'containment': 'containment_install_date',
        'cart_manager': 'cart_manager_install_date',
        'subscription': 'subscription_start_date',
        'paired_wheel': 'paired_wheel_install_date'
      };
      const dateField = filterMap[solutionFilter];
      if (dateField) {
        conditions.push(`pois: { ${dateField}: { _is_null: false } }`);
      }
    }

    // Add notes filter if sortBy is 'With Notes'
    if (sortBy === 'With Notes') {
      conditions.push(`brand_notes: {}`);
    }

    // Build final where clause
    if (conditions.length > 0) {
      whereClause = `, where: { ${conditions.join(', ')} }`;
    }

    // Build order_by clause based on sortBy
    let orderByClause = '';

    if (sortBy === 'With Notes') {
      // Sort by top retailers first, then by name
      orderByClause = '{is_top_retailer: desc}';
    } else if (sortBy === 'Top Retailers') {
      // Sort by top retailers first, then by name
      orderByClause = '{is_top_retailer: desc}';
    } else if (sortBy === 'Name (A-Z)') {
      // Sort alphabetically by name ascending
      orderByClause = '{name: asc}';
    } else if (sortBy === 'Name (Z-A)') {
      // Sort alphabetically by name descending
      orderByClause = '{name: desc}';
    } else {
      // Default 'Opportunity Size' - sort by whitespace count (descending)
      orderByClause = '{pois_aggregate: {count: desc}}';
    }

    const query = `
      query MyQuery($limit: Int!, $offset: Int!) {
        udm_brand(limit: $limit, offset: $offset, order_by: ${orderByClause}${whereClause}) {
          is_top_retailer
          name
          estimated_no_of_carts_per_store

          total_pois: pois_aggregate(where: {closed_on: {_is_null: true}}) {
            aggregate {
              count
            }
          }

          customers: pois_aggregate(where: {_and: [{closed_on: {_is_null: true}}, {customer_id: {_is_null: false}}]}) {
            aggregate {
              count
            }
          }

          purchek_recommended: pois_aggregate(
            where: {
              _and: [
                { closed_on: { _is_null: true } }
                { purchek_install_date: { _is_null: true } }
                {
                  _or: [
                    { nearby_penetration: { purchek_customer_count: { _gte: ${purchekThresholds.neighborhoodSites} } } }
                    { nearby_penetration: { penetration_purchek: { _gte: ${purchekThresholds.penetration} } } }
                    { geo: { total_crime_incidents: { _gte: ${purchekThresholds.crimeIncidents} } } }
                  ]
                }
              ]
            }
          ) {
            aggregate {
              count
            }
          }

          containment_recommended: pois_aggregate(
            where: {
              _and: [
                { closed_on: { _is_null: true } }
                { containment_install_date: { _is_null: true } }
                {
                  _or: [
                    { nearby_penetration: { containment_customer_count: { _gte: ${containmentThresholds.neighborhoodSites} } } }
                    { nearby_penetration: { penetration_containment: { _gte: ${containmentThresholds.penetration} } } }
                    { geo: { total_crime_incidents: { _gte: ${containmentThresholds.crimeIncidents} } } }
                    { geo: {penalty_for_cart_owner: { _eq: ${containmentThresholds.ordinance > 0 ? 'true' : 'false'} } }}
                    { nearby_penetration: { plaza_cm_customer_count: { _gt: ${containmentThresholds.plazaSites} } } }
                  ]
                }
              ]
            }
          ) {
            aggregate {
              count
            }
          }

          cart_manager_recommended: pois_aggregate(
            where: {
              _and: [
                { closed_on: { _is_null: true } }
                { cart_manager_install_date: { _is_null: true } }
                {
                  _or: [
                    { brand: { estimated_no_of_carts_per_store: { _gte: ${cartManagerThresholds.estimatedCarts} } } }
                    { geo: { no_of_cold_days: { _gte: ${cartManagerThresholds.coldDays} } } }
                    { geo: { no_of_hot_days: { _gte: ${cartManagerThresholds.hotDays} } } }
                  ]
                }
              ]
            }
          ) {
            aggregate {
              count
            }
          }

          subscription_recommended: pois_aggregate(
            where: {
              _and: [
                { closed_on: { _is_null: true } }
                { subscription_start_date: { _is_null: true } }
                { purchek_install_date: { _is_null: false } }
              ]
            }
          ) {
            aggregate {
              count
            }
          }

          paired_wheel_recommended: pois_aggregate(
            where: {
              _and: [
                { closed_on: { _is_null: true } }
                { paired_wheel_install_date: { _is_null: true } }
                { nearby_penetration: { penetration_paired_wheel: { _gte: 75 } } }
              ]
            }
          ) {
            aggregate {
              count
            }
          }

        }
      }
    `;

    const response = await graphqlClient.request<BrandResponse>(query, {
      limit,
      offset
    });

    return response.udm_brand;
  } catch (error: any) {
    // Return empty array on error instead of throwing
    return [];
  }
};

export interface UltimateParentFilters {
  retailerSegment: string | string[] | null;
  opportunitiesMin: number;
  opportunitiesMax: number;
  retailerSitesMin: number;
  retailerSitesMax: number;
}

export interface UltimateParentResponse {
  data: BrandData[];
  totalCount: number;
}

/**
 * Fetch unique ultimate parents with aggregated metrics from all their child brands
 * Using custom REST API endpoint (Hasura Action backend)
 */
export const fetchUltimateParentData = async (
  limit: number = 10,
  offset: number = 0,
  sortBy: string = 'Top Retailers',
  filters?: UltimateParentFilters,
  purchekConfig?: any,
  containmentConfig?: any,
  cartManagerConfig?: any
): Promise<UltimateParentResponse> => {
  try {
    // Get active configuration from store
    const store = useConfigurationStore.getState();

    // Use provided config or get from store
    const activePurchekConfig = purchekConfig || store.purchek;
    const activeContainmentConfig = containmentConfig || store.containment;
    const activeCartManagerConfig = cartManagerConfig || store.cartManager;

    const mapSortToOrder = (sort: string): string => {
      if (sort === 'Brand Name (Z-A)') return 'desc';
      if (sort === 'Total Carts') return 'carts';
      return 'asc';
    };

    const payload = {
      limit,
      offset,
      sortOrder: mapSortToOrder(sortBy),
      retailerSegment: filters?.retailerSegment || null,
      opportunitiesMin: filters?.opportunitiesMin || 0,
      opportunitiesMax: filters?.opportunitiesMax || 999999999,
      retailerSitesMin: filters?.retailerSitesMin || 0,
      retailerSitesMax: filters?.retailerSitesMax || 999999999,
      thresholds: {
        purchekThresholds: {
          neighborhoodSites: activePurchekConfig.neighborhoodSites || 5,
          penetration: activePurchekConfig.penetration || 75,
          crimeIncidents: activePurchekConfig.crimeIncidents || 5000
        },
        containmentThresholds: {
          neighborhoodSites: activeContainmentConfig.neighborhoodSites || 5,
          penetration: activeContainmentConfig.penetration || 75,
          crimeIncidents: activeContainmentConfig.crimeIncidents || 5000,
          ordinance: activeContainmentConfig.ordinance || 1,
          plazaSites: activeContainmentConfig.plazaSites || 0
        },
        cartManagerThresholds: {
          estimatedCarts: activeCartManagerConfig.estimatedCarts || 3,
          coldDays: activeCartManagerConfig.coldDays || 90,
          hotDays: activeCartManagerConfig.hotDays || 90
        }
      }
    };

    // Call getUltimateParents Hasura Action via GraphQL Query
    const response = await fetch(environment.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
      },
      body: JSON.stringify({
        query: `
          query getUltimateParents($input: GetUltimateParentsInput!) {
            getUltimateParents(input: $input) {
              success
              data {
                name
                ultimate_parent
                is_top_retailer
                estimated_no_of_carts_per_store
                total_pois {
                  aggregate {
                    count
                  }
                }
                customers {
                  aggregate {
                    count
                  }
                }
                purchek_recommended {
                  aggregate {
                    count
                  }
                }
                containment_recommended {
                  aggregate {
                    count
                  }
                }
                cart_manager_recommended {
                  aggregate {
                    count
                  }
                }
                subscription_recommended {
                  aggregate {
                    count
                  }
                }
                paired_wheel_recommended {
                  aggregate {
                    count
                  }
                }
                purchek_acquired {
                  aggregate {
                    count
                  }
                }
                containment_acquired {
                  aggregate {
                    count
                  }
                }
                cart_manager_acquired {
                  aggregate {
                    count
                  }
                }
                subscription_acquired {
                  aggregate {
                    count
                  }
                }
                paired_wheel_acquired {
                  aggregate {
                    count
                  }
                }
                number_of_opportunities
              }
              totalCount
            }
          }
        `,
        variables: { input: payload }
      })
    });

    const result = await response.json();

    if (result.errors) {
      console.error('Error fetching ultimate parents:', result.errors);
      return { data: [], totalCount: 0 };
    }

    const actionResult = result.data?.getUltimateParents;

    if (!actionResult?.success) {
      console.error('Action failed:', actionResult);
      return { data: [], totalCount: 0 };
    }

    return {
      data: actionResult.data || [],
      totalCount: actionResult.totalCount || 0
    };
  } catch (error: any) {
    console.error('fetchUltimateParentData error:', error);
    return { data: [], totalCount: 0 };
  }
};

export interface BrandSearchResult {
  name: string;
  ultimate_parent?: string;
  type: 'brand' | 'parent';
}

/**
 * Lightweight search function for brand autocomplete
 * Searches only brand names and ultimate parents without complex filters
 * Returns up to 10 most relevant matches
 */
export const searchBrands = async (searchTerm: string): Promise<BrandSearchResult[]> => {
  if (!searchTerm || searchTerm.trim().length === 0) {
    return [];
  }

  try {
    const query = `
      query SearchBrands($searchTerm: String!) {
        brands: udm_brand(
          where: {
            _or: [
              { name: { _ilike: $searchTerm } }
              { ultimate_parent: { _ilike: $searchTerm } }
            ]
          }
          order_by: { name: asc }
          limit: 50
        ) {
          name
          ultimate_parent
        }
      }
    `;

    const response = await graphqlClient.request<{
      brands: Array<{ name: string; ultimate_parent?: string }>;
    }>(query, {
      searchTerm: `%${searchTerm}%`
    });

    // Process results separately for brands and parents to allow duplicates with different types
    const brandResults: (BrandSearchResult & { relevance: number })[] = [];
    const parentResults: (BrandSearchResult & { relevance: number })[] = [];
    const searchLower = searchTerm.toLowerCase();

    // Calculate relevance score for a string
    const calculateRelevance = (text: string, search: string): number => {
      const textLower = text.toLowerCase();
      // Exact match = 100
      if (textLower === search) return 100;
      // Starts with search = 80
      if (textLower.startsWith(search)) return 80;
      // Contains search = 60
      if (textLower.includes(search)) return 60;
      return 0;
    };

    // Process brands
    response.brands.forEach(brand => {
      const brandRelevance = calculateRelevance(brand.name, searchLower);

      if (brandRelevance > 0) {
        brandResults.push({
          name: brand.name,
          ultimate_parent: brand.ultimate_parent,
          type: 'brand',
          relevance: brandRelevance
        });
      }

      // Also add ultimate parent if it matches
      if (brand.ultimate_parent) {
        const parentRelevance = calculateRelevance(brand.ultimate_parent, searchLower);
        if (parentRelevance > 0) {
          // Check if this parent is already in the list to avoid duplicates within parents
          const existingParent = parentResults.find(p => p.name === brand.ultimate_parent);
          if (!existingParent || existingParent.relevance < parentRelevance) {
            // Remove the existing one if it has lower relevance
            const index = parentResults.findIndex(p => p.name === brand.ultimate_parent);
            if (index >= 0) {
              parentResults.splice(index, 1);
            }
            parentResults.push({
              name: brand.ultimate_parent,
              type: 'parent',
              relevance: parentRelevance
            });
          }
        }
      }
    });

    // Filter out ultimate parents that have only one brand
    let filteredParentResults = parentResults;
    if (parentResults.length > 0) {
      const parentNames = parentResults.map(p => p.name);
      const countQuery = `
        query CountBrandsPerParent($parents: [String!]!) {
          udm_brand(where: { ultimate_parent: { _in: $parents } }) {
            name
            ultimate_parent
          }
        }
      `;
      try {
        const countResponse = await graphqlClient.request<{
          udm_brand: Array<{ name: string; ultimate_parent: string }>;
        }>(countQuery, { parents: parentNames });

        const parentBrandCounts = new Map<string, number>();
        countResponse.udm_brand.forEach(brand => {
          const count = parentBrandCounts.get(brand.ultimate_parent) || 0;
          parentBrandCounts.set(brand.ultimate_parent, count + 1);
        });

        // Only keep parents with more than one brand
        filteredParentResults = parentResults.filter(p => (parentBrandCounts.get(p.name) || 0) > 1);
      } catch {
        // If count query fails, keep all parent results
      }
    }

    // Combine and sort by relevance (highest first), then by type (brand first)
    const results = [...brandResults, ...filteredParentResults]
      .sort((a, b) => {
        // Sort by relevance first (highest first)
        if (b.relevance !== a.relevance) {
          return b.relevance - a.relevance;
        }
        // If relevance is same, brands come before parents
        if (a.type === 'brand' && b.type === 'parent') return -1;
        if (a.type === 'parent' && b.type === 'brand') return 1;
        return 0;
      })
      .map(({ relevance, ...item }) => item) // Remove relevance from final result
      .slice(0, 20);

    return results;
  } catch (error: any) {
    console.error('Brand search error:', error);
    return [];
  }
};

/**
 * Fetch all brands that belong to a specific ultimate parent
 */
export const fetchBrandsByUltimateParent = async (ultimateParent: string): Promise<BrandData[]> => {
  try {
    const query = `
      query getBrandsByUltimateParent($parent: String!) {
        udm_brand(where: {ultimate_parent: {_eq: $parent}}, order_by: {pois_aggregate: {count: desc}}) {
          name
          is_top_retailer
          estimated_no_of_carts_per_store
          total_pois: pois_aggregate(where: {closed_on: {_is_null: true}}) {
            aggregate {
              count
            }
          }
          customers: pois_aggregate(where: {_and: [{closed_on: {_is_null: true}}, {customer_id: {_is_null: false}}]}) {
            aggregate {
              count
            }
          }
          purchek_recommended: pois_aggregate(
            where: {
              _and: [
                { closed_on: { _is_null: true } }
                { purchek_install_date: { _is_null: true } }
              ]
            }
          ) {
            aggregate {
              count
            }
          }
          containment_recommended: pois_aggregate(
            where: {
              _and: [
                { closed_on: { _is_null: true } }
                { containment_install_date: { _is_null: true } }
              ]
            }
          ) {
            aggregate {
              count
            }
          }
          cart_manager_recommended: pois_aggregate(
            where: {
              _and: [
                { closed_on: { _is_null: true } }
                { cart_manager_install_date: { _is_null: true } }
              ]
            }
          ) {
            aggregate {
              count
            }
          }
          subscription_recommended: pois_aggregate(
            where: {
              _and: [
                { closed_on: { _is_null: true } }
                { subscription_start_date: { _is_null: true } }
              ]
            }
          ) {
            aggregate {
              count
            }
          }
          paired_wheel_recommended: pois_aggregate(
            where: {
              _and: [
                { closed_on: { _is_null: true } }
                { paired_wheel_install_date: { _is_null: true } }
              ]
            }
          ) {
            aggregate {
              count
            }
          }
        }
      }
    `;

    const response = await graphqlClient.request(query, { parent: ultimateParent });
    return response.udm_brand || [];
  } catch (error: any) {
    console.error('Error fetching brands by ultimate parent:', error);
    return [];
  }
};

/**
 * Aggregate brand data from multiple brands
 */
export const aggregateBrandData = (brands: BrandData[]): BrandData => {
  if (brands.length === 0) {
    return {
      name: 'Unknown',
      total_pois: { aggregate: { count: 0 } },
      customers: { aggregate: { count: 0 } },
      purchek_recommended: { aggregate: { count: 0 } },
      containment_recommended: { aggregate: { count: 0 } },
      cart_manager_recommended: { aggregate: { count: 0 } },
      subscription_recommended: { aggregate: { count: 0 } },
      paired_wheel_recommended: { aggregate: { count: 0 } },
      purchek_acquired: { aggregate: { count: 0 } },
      containment_acquired: { aggregate: { count: 0 } },
      cart_manager_acquired: { aggregate: { count: 0 } },
      subscription_acquired: { aggregate: { count: 0 } },
      paired_wheel_acquired: { aggregate: { count: 0 } },
      brand_notes: [],
      brand_notes_count: { aggregate: { count: 0 } }
    };
  }

  if (brands.length === 1) {
    return brands[0];
  }

  // Sum up all metrics from multiple brands
  const aggregated: BrandData = {
    name: brands[0].name || 'Unknown',
    is_top_retailer: brands.some(b => b.is_top_retailer),
    estimated_no_of_carts_per_store: Math.round(
      brands.reduce((sum, b) => sum + (b.estimated_no_of_carts_per_store || 0), 0) / brands.length
    ),
    total_pois: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.total_pois?.aggregate?.count || 0), 0)
      }
    },
    customers: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.customers?.aggregate?.count || 0), 0)
      }
    },
    purchek_recommended: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.purchek_recommended?.aggregate?.count || 0), 0)
      }
    },
    containment_recommended: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.containment_recommended?.aggregate?.count || 0), 0)
      }
    },
    cart_manager_recommended: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.cart_manager_recommended?.aggregate?.count || 0), 0)
      }
    },
    subscription_recommended: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.subscription_recommended?.aggregate?.count || 0), 0)
      }
    },
    paired_wheel_recommended: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.paired_wheel_recommended?.aggregate?.count || 0), 0)
      }
    },
    purchek_acquired: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.purchek_acquired?.aggregate?.count || 0), 0)
      }
    },
    containment_acquired: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.containment_acquired?.aggregate?.count || 0), 0)
      }
    },
    cart_manager_acquired: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.cart_manager_acquired?.aggregate?.count || 0), 0)
      }
    },
    subscription_acquired: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.subscription_acquired?.aggregate?.count || 0), 0)
      }
    },
    paired_wheel_acquired: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.paired_wheel_acquired?.aggregate?.count || 0), 0)
      }
    },
    brand_notes: brands.flatMap(b => b.brand_notes || []),
    brand_notes_count: {
      aggregate: {
        count: brands.reduce((sum, b) => sum + (b.brand_notes_count?.aggregate?.count || 0), 0)
      }
    }
  };

  return aggregated;
};